import { Link, type LinkProps } from '@tanstack/react-router';

import { AppIcons, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

export type BreadcrumbItem = {
  label: string;
  to?: LinkProps['to'];
  params?: LinkProps['params'];
  icon?: AppIconName;
};

export type BreadcrumbProps = {
  items: BreadcrumbItem[];
  ariaLabel?: string;
};

/**
 * Breadcrumb — uppercase mono crumbtrail matching the `.crumb` /
 * `.ov-crumb` / `.cfg-crumb` family in the design bundle. Locked to
 * the canonical 11px / 0.06em tracking / fg-muted text style; the
 * component does not accept a `className` prop.
 */
export function Breadcrumb({
  items,
  ariaLabel = 'breadcrumb',
}: Readonly<BreadcrumbProps>): React.JSX.Element {
  return (
    <nav
      aria-label={ariaLabel}
      className="inline-flex min-w-0 items-center gap-2 font-mono text-card-id uppercase tracking-eyebrow text-muted-foreground whitespace-nowrap"
    >
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <BreadcrumbCrumb
            key={`${item.label}-${String(idx)}`}
            item={item}
            isLast={isLast}
            isFirst={idx === 0}
          />
        );
      })}
    </nav>
  );
}

type CrumbProps = {
  item: BreadcrumbItem;
  isLast: boolean;
  isFirst: boolean;
};

function BreadcrumbCrumb({ item, isLast, isFirst }: Readonly<CrumbProps>): React.JSX.Element {
  const labelClassName = twcn('truncate', isLast && 'font-medium text-foreground');
  const labelEl =
    item.to && !isLast ? (
      <Link
        to={item.to}
        params={item.params as never}
        className={twcn(labelClassName, 'hover:text-foreground')}
      >
        {item.label}
      </Link>
    ) : (
      <span className={labelClassName}>{item.label}</span>
    );
  return (
    <>
      {isFirst ? null : <span className="text-faint-foreground">/</span>}
      {item.icon ? <AppIcons name={item.icon} size={14} className="text-faint-foreground" /> : null}
      {labelEl}
    </>
  );
}
